<template>
  <el-dialog
      title="模型性能分析"
      v-model="dialogVisible"
      width="80%"
      @close="handleClose"
      @opened="onDialogOpened"
      destroy-on-close
      :before-close="handleBeforeClose"
  >
    <div
        v-loading="loading"
        element-loading-text="正在分析模型性能，请稍候..."
        element-loading-spinner="el-icon-loading"
        element-loading-background="rgba(0, 0, 0, 0.7)"
        element-loading-custom-class="analysis-loading"
    >
      <!-- 只有数据存在时才渲染图表容器 -->
      <template v-if="analysisData">
        <!-- 整体混淆矩阵 -->
        <h3>整体混淆矩阵（所有位置）</h3>
        <div ref="confusionChart" style="height: 400px; width: 100%;"></div>

        <!-- 各位置混淆矩阵标签页 -->
        <el-tabs
            v-model="activePos"
            v-if="analysisData.per_position && analysisData.per_position.length > 0"
        >
          <el-tab-pane
              v-for="(pos, index) in analysisData.per_position"
              :key="index"
              :label="`位置 ${index + 1}`"
              :name="index.toString()"
          >
            <div :ref="(el) => setPosRef(index, el)" style="height: 300px; width: 100%;"></div>
          </el-tab-pane>
        </el-tabs>

        <!-- 字符错误率柱状图 -->
        <h3>字符级错误率</h3>
        <div ref="charErrorChart" style="height: 400px; width: 100%;"></div>

        <!-- 样本难度分布 -->
        <h3>样本难度分布（置信度）</h3>
        <div ref="difficultyChart" style="height: 300px; width: 100%;"></div>
      </template>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, watch, nextTick, onBeforeUnmount } from 'vue'
import * as echarts from 'echarts'
import { getModelAnalysis } from '@/api/captcha'
import { ElMessage } from 'element-plus'

const props = defineProps({
  filename: String,
  visible: Boolean,
  resultData: Object  // 新增：直接传入分析数据，优先使用
})
const emit = defineEmits(['update:visible'])

const dialogVisible = ref(props.visible)
const loading = ref(false)
const analysisData = ref(null)
const activePos = ref('0')

// 图表 DOM 引用
const confusionChart = ref(null)
const charErrorChart = ref(null)
const difficultyChart = ref(null)
// 各位置 DOM 收集
const posRefs = ref([])
const setPosRef = (index, el) => {
  posRefs.value[index] = el
}

let charts = []
let renderRetryTimer = null

// 带重试的数据加载（仅当没有 resultData 时使用）
const loadDataWithRetry = async (retryCount = 0) => {
  try {
    const resp = await getModelAnalysis(props.filename)
    if (resp.data.code === 200) {
      analysisData.value = resp.data.data
      await nextTick()
      attemptRender()
    } else {
      if (retryCount < 2) {
        setTimeout(() => loadDataWithRetry(retryCount + 1), 1000)
      } else {
        ElMessage.error(resp.data.msg || '获取分析数据失败')
        loading.value = false
      }
    }
  } catch (e) {
    if (retryCount < 2) {
      setTimeout(() => loadDataWithRetry(retryCount + 1), 1000)
    } else {
      ElMessage.error('请求失败，请检查网络')
      console.error(e)
      loading.value = false
    }
  }
}

// 尝试渲染所有图表（带重试）
const attemptRender = (maxRetries = 10, delay = 200) => {
  if (renderRetryTimer) clearTimeout(renderRetryTimer)

  const checkAndRender = (retryCount) => {
    const mainReady = confusionChart.value && confusionChart.value.clientWidth > 0
    const charReady = charErrorChart.value && charErrorChart.value.clientWidth > 0
    const diffReady = difficultyChart.value && difficultyChart.value.clientWidth > 0
    const activePosIndex = parseInt(activePos.value)
    const posReady = posRefs.value[activePosIndex] && posRefs.value[activePosIndex].clientWidth > 0

    if (mainReady && charReady && diffReady && posReady) {
      renderCharts()
      loading.value = false
    } else if (retryCount < maxRetries) {
      renderRetryTimer = setTimeout(() => checkAndRender(retryCount + 1), delay)
    } else {
      console.warn('部分容器宽度为0，强制渲染')
      renderCharts()
      loading.value = false
    }
  }

  checkAndRender(0)
}

// 渲染所有图表
const renderCharts = () => {
  if (!analysisData.value) return

  // 整体混淆矩阵
  const totalConfusion = mergeConfusionMatrices(analysisData.value.per_position)
  if (confusionChart.value) {
    renderConfusionMatrix(totalConfusion, '整体混淆矩阵', confusionChart.value)
  }

  // 仅渲染当前激活位置的混淆矩阵
  const activePosIndex = parseInt(activePos.value)
  const posDom = posRefs.value[activePosIndex]
  if (posDom && analysisData.value.per_position[activePosIndex]) {
    renderConfusionMatrix(
        analysisData.value.per_position[activePosIndex],
        `位置 ${activePosIndex + 1} 混淆矩阵`,
        posDom
    )
  }

  renderCharErrorChart()
  renderDifficultyChart()
}

// 合并混淆矩阵
const mergeConfusionMatrices = (perPos) => {
  if (!perPos || perPos.length === 0) return []
  const merged = perPos[0].map(row => [...row])
  for (let i = 1; i < perPos.length; i++) {
    for (let r = 0; r < merged.length; r++) {
      for (let c = 0; c < merged[r].length; c++) {
        merged[r][c] += perPos[i][r][c]
      }
    }
  }
  return merged
}

// 渲染混淆矩阵（热力图）
const renderConfusionMatrix = (matrix, title, dom) => {
  if (!dom || !matrix || matrix.length === 0) return
  const existing = charts.find(chart => chart.getDom() === dom)
  if (existing) {
    existing.dispose()
    charts = charts.filter(ch => ch !== existing)
  }
  const chart = echarts.init(dom)
  charts.push(chart)

  const charList = analysisData.value.char_list || []
  const maxValue = Math.max(...matrix.flat())

  const option = {
    title: { text: title, left: 'center' },
    tooltip: {
      position: 'top',
      formatter: (params) => {
        const trueChar = charList[params.value[0]] || ''
        const predChar = charList[params.value[1]] || ''
        return `真实: ${trueChar}<br/>预测: ${predChar}<br/>数量: ${params.value[2]}`
      }
    },
    grid: { left: '5%', right: '5%', bottom: '10%', top: '15%', containLabel: true },
    xAxis: { type: 'category', data: charList, splitArea: { show: true } },
    yAxis: { type: 'category', data: charList, splitArea: { show: true } },
    visualMap: {
      min: 0,
      max: maxValue,
      calculable: true,
      orient: 'horizontal',
      left: 'center'
    },
    series: [{
      name: '混淆矩阵',
      type: 'heatmap',
      data: matrix.flatMap((row, i) => row.map((value, j) => [i, j, value])),
      label: {
        show: true,
        formatter: (params) => params.value[2] > 0 ? params.value[2] : ''
      }
    }]
  }
  chart.setOption(option)
}

// 渲染字符错误率柱状图
const renderCharErrorChart = () => {
  if (!charErrorChart.value || !analysisData.value.char_stats) return
  const existing = charts.find(chart => chart.getDom() === charErrorChart.value)
  if (existing) {
    existing.dispose()
    charts = charts.filter(ch => ch !== existing)
  }
  const charStats = analysisData.value.char_stats
  const chars = Object.keys(charStats)
  const errorRates = chars.map(c => {
    const stat = charStats[c]
    return ((stat.total - stat.correct) / stat.total * 100).toFixed(1)
  })

  const chart = echarts.init(charErrorChart.value)
  charts.push(chart)

  const option = {
    title: { text: '字符错误率', left: 'center' },
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    xAxis: { type: 'category', data: chars, axisLabel: { rotate: 45 } },
    yAxis: { type: 'value', name: '错误率 (%)', max: 100 },
    series: [{
      name: '错误率',
      type: 'bar',
      data: errorRates,
      itemStyle: { color: '#f56c6c' }
    }]
  }
  chart.setOption(option)
}

// 渲染样本难度分布（箱线图）
const renderDifficultyChart = () => {
  if (!difficultyChart.value || !analysisData.value.sample_difficulty) return
  const existing = charts.find(chart => chart.getDom() === difficultyChart.value)
  if (existing) {
    existing.dispose()
    charts = charts.filter(ch => ch !== existing)
  }
  const samples = analysisData.value.sample_difficulty
  const correctConfs = samples.filter(s => s.type === 'correct').map(s => s.conf)
  const errorConfs = samples.filter(s => s.type === 'error').map(s => s.conf)

  const chart = echarts.init(difficultyChart.value)
  charts.push(chart)

  const option = {
    title: { text: '样本置信度分布' },
    tooltip: { trigger: 'item' },
    xAxis: { type: 'category', data: ['正确样本', '错误样本'] },
    yAxis: { type: 'value', name: '置信度', min: 0, max: 1 },
    series: [
      {
        name: '置信度分布',
        type: 'boxplot',
        data: [
          correctConfs.length ? correctConfs : [0],
          errorConfs.length ? errorConfs : [0]
        ],
        itemStyle: {
          color: (params) => params.dataIndex === 0 ? '#67c23a' : '#f56c6c'
        }
      }
    ]
  }
  chart.setOption(option)
}

const onDialogOpened = () => {
  attemptRender()
}

// 监听标签页切换
watch(activePos, (newPos) => {
  if (!analysisData.value) return
  const index = parseInt(newPos)
  const dom = posRefs.value[index]
  if (dom && analysisData.value.per_position[index]) {
    nextTick(() => {
      renderConfusionMatrix(
          analysisData.value.per_position[index],
          `位置 ${index + 1} 混淆矩阵`,
          dom
      )
    })
  }
})

// 监听 visible 和 resultData/filename 变化
watch(
    () => [props.visible, props.resultData, props.filename],
    async ([val, resultData, filename]) => {
      dialogVisible.value = val
      if (!val) {
        analysisData.value = null
        return
      }
      if (resultData) {
        // 直接使用传入的数据
        analysisData.value = resultData
        loading.value = false
        await nextTick()
        attemptRender()
      } else if (filename) {
        // 通过 filename 获取数据
        loading.value = true
        analysisData.value = null
        charts.forEach(chart => chart.dispose())
        charts = []
        await loadDataWithRetry()
      }
    },
    { immediate: true }
)

const handleBeforeClose = (done) => {
  if (renderRetryTimer) clearTimeout(renderRetryTimer)
  charts.forEach(chart => chart.dispose())
  charts = []
  done()
}

const handleClose = () => {
  emit('update:visible', false)
}

onBeforeUnmount(() => {
  if (renderRetryTimer) clearTimeout(renderRetryTimer)
  charts.forEach(chart => chart.dispose())
  charts = []
})
</script>

<style scoped>
:deep(.analysis-loading .el-loading-text) {
  color: #fff;
  font-size: 18px;
  font-weight: bold;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
}
</style>